import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { jsPDF } from "jspdf";
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Input, Spin, SelectProps, Select, InputNumber, App, Upload, TreeSelect, Switch, Divider, TimePicker, Space, Typography } from "antd"
import { DatePicker } from "antd-jalali"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaPrint, FaRegSave, FaInbox } from "react-icons/fa"
import { API, Enum, Regex, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"

const currency = new Intl.NumberFormat()
const { Pages: { TicketManagement: { Ticket: TicketLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { BASIC_INFORMATION_PROVINCE_CITIES, TICKET_MANAGEMENT_TICKET_ID, TICKET_MANAGEMENT_PRICE_DESTINATION, TICKET_MANAGEMENT_TICKET_CREATE, TICKET_MANAGEMENT_TICKET_UPDATE, USER_MANAGEMENT_USER_USERNAME, USER_MANAGEMENT_USER_UPDATE, USER_MANAGEMENT_USER_CREATE, TICKET_MANAGEMENT_COMPANY_ALL } = API
const { Message, Payment } = Enum
const { isNumber, notEnglish } = Regex
const { IRANSans, IRANSansFaNum } = Font

interface ProvincesType {
    id: string,
    name: string,
    cities: CityType[],
}

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface TicketType {
    row: number,
    id: string,
    key: string,
    number: string,
    user: UserType,
    operator?: UserType,
    destination : CityType,
    company: CompanyType,
    seat: number,
    amount: number,
    outCycle: boolean,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface TicketFormType {
    destination: string,
    company: string,
    seat: number,
    user?: string,
    mobile: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
}

export default function CURecord() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const navigate = useNavigate()
    const { accessToken, baseURL, meta } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(true)
    const [provinces, setProvinces] = useState<ProvincesType[]>([])
    const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false)
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [loadingRecord, setLoadingRecord] = useState<boolean>(false)
    const [user, setUser] = useState<UserType|null>(null)
    const [loadingUser, setLoadingUser] = useState<boolean>(false)
    const [amount, setAmount] = useState<number>(0)
    const [loadingAmount, setLoadingAmount] = useState<boolean>(false)
    const { id: ticketId } = useParams()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])


    useEffect(() => {
        form.setFieldsValue({ amount });
    }, [amount])

    const initialAPI = async () => {
        await getProvinces()
        if (ticketId) {
            setLoadingRecord(true);
            await getRecord(ticketId)
        }
    }

    const generatePassword = (length = 8): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

        const randomValues = new Uint32Array(length);
        window.crypto.getRandomValues(randomValues);

        return Array.from(randomValues)
            .map(v => chars[v % chars.length])
            .join('');
    }

    const getCompanies = async (city: string) => {
        setLoadingCompanies(true);
        await axios.post(TICKET_MANAGEMENT_COMPANY_ALL, { city, publish: true }).then((response: { data: CompanyType[] }) => {
            const { data: companies } = response
            setCompanies(_.orderBy(companies.map((company) => {
                const { tickets} = company;
                const { count: seatOne = 0 } = _.findLast(tickets, { seat: 1 }) || {};
                const { count: seatTwo = 0 } = _.findLast(tickets, { seat: 2 }) || {};
                const { count: seatThree = 0 } = _.findLast(tickets, { seat: 3 }) || {};
                let countService = 0;
                let seatLeft = 0;
                let carLeft = 0;
                if (seatOne >= seatTwo) {
                    seatLeft = (seatOne - seatTwo) % 3;
                    seatLeft = seatLeft > 0 ? (3 - seatLeft) : 0;
                    carLeft = seatLeft > 0 ? 1 : 0;
                } else {
                    carLeft = seatTwo - seatOne;
                    seatLeft = carLeft > 0 ? 1 : 0;
                }
                if (seatThree > 0 || (seatOne > 0 && seatTwo > 0) || seatOne > 2) {
                    if (seatThree > 0) countService = countService + seatThree;
                    if (seatOne > 0 && seatTwo > 0) {
                        if (seatOne >= seatTwo)
                            countService = countService + seatTwo + Math.floor((seatOne - seatTwo) / 3);
                        else countService = countService + seatOne;
                    } else if (seatOne > 2) {
                        countService = countService + Math.floor(seatOne / 3);
                    }
                }

                return {
                    ...company,
                    seatOne,
                    seatTwo,
                    seatThree,
                    seatLeft,
                    carLeft,
                    countService,
                }
            }), ['priority', 'seatLeft', 'countService', 'position'],  ['desc', 'desc', 'asc', 'asc']))
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        });
        setLoadingCompanies(false);
    }

    const getUser = async (mobile: string) => axios.get(USER_MANAGEMENT_USER_USERNAME + mobile).then(async (response: { data: UserType }) => {
        const { data: user } = response
        form.setFieldsValue({
            user: user?.id,
            firstName: user?.firstName,
            lastName: user?.lastName,
            nationalCode: user?.nationalCode,
        })
        setUser(user);
        setLoadingUser(false)
    }).catch((error: AxiosError) => {
        setUser(null);
        setLoadingUser(false)
    })

    const getProvinces = async () => axios.get(BASIC_INFORMATION_PROVINCE_CITIES).then((response: { data: ProvincesType[] }) => {
        const { data: provinces } = response
        setProvinces(provinces)
        setLoadingProvinces(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const getRecord = async (ticketId: string): Promise<TicketType> => axios.get(TICKET_MANAGEMENT_TICKET_ID + ticketId).then(async (response: { data: TicketType }) => {
        const { data: ticket } = response
        setUser(ticket.user)
        ticket.destination.id && await getCompanies(ticket.destination.id);
        ticket.amount && setAmount(ticket.amount);
        form.setFieldsValue({ ...ticket, ...ticket.user, user: ticket.user.id, mobile: `0${ticket.user.mobile}`, destination: ticket.destination.id, company: ticket.company.id })
        setLoadingRecord(false)
        return ticket;
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const getDestination = async (destinationId: string, seat: number = 0): Promise<number> => {
        setLoadingAmount(true);
        return axios.get(TICKET_MANAGEMENT_PRICE_DESTINATION + destinationId).then(async (response: { data: number }) => {
            const { data: price } = response
            setAmount(seat * price)
            setLoadingAmount(false)
            return amount;
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            setLoadingAmount(false)
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        })
    }

    const upsertTicket  = async (ticket: TicketFormType) => {
        if (ticketId) {
            axios.patch(TICKET_MANAGEMENT_TICKET_UPDATE + ticketId, ticket).then(async (response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                await getRecord(ticketId)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            axios.post(TICKET_MANAGEMENT_TICKET_CREATE, ticket).then(async (response: { data: { id: string } }) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                const { data: { id } } = response
                navigate('/ticket-management/ticket/edit/' + id)
                await getRecord(id)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    const upsertUser  = async (ticket: TicketFormType): Promise<UserType> => {
        if (ticket.user) {
            return axios.patch(USER_MANAGEMENT_USER_UPDATE + ticket.user, ticket).then(async (response: { data: UserType }) => {
                const { data: user } = response;
                form.setFieldsValue({
                    user: user?.id,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    nationalCode: user?.nationalCode,
                });
                return user
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            return axios.post(USER_MANAGEMENT_USER_CREATE, { ...ticket, mobile: ticket.mobile.substring(1), password: generatePassword() }).then(async (response: { data: UserType }) => {
                const { data: user } = response;
                form.setFieldsValue({
                    user: user?.id,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    nationalCode: user?.nationalCode,
                })
                return user;
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    const onFinish = async (ticket: TicketFormType) => {
        setLoadingRecord(true);
        const user = await upsertUser(ticket);
        if (user) await upsertTicket({ ...ticket, user: user.id });
        else setLoadingRecord(false);
    }

    return (
        <div id={'ticket'}>
            <Spin spinning={loadingRecord || loadingProvinces || loadingUser || loadingAmount || loadingCompanies} tip={Share.loading} size="large">
                <Form form={form} name="cu-record" onFinish={onFinish} style={{ maxWidth: 450 }} disabled={!!ticketId}>
                    <Form.Item name="user" style={{display:'none'}}>
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.mobile}
                        name="mobile"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.mobile} ${Validation.required}` },
                            { pattern: isNumber, message: `${TicketLanguage.mobile} ${Validation.pattern}` },
                            { len: 11, message: `${TicketLanguage.mobile} 11 ${Validation.length}` },
                        ]}
                    >
                        <Input
                            type="tel"
                            maxLength={11}
                            placeholder={TicketLanguage.mobile}
                            onBlur={async (event: any) => {
                                const mobile = event?.target?.value;
                                form.resetFields(['user', 'firstName', 'lastName', 'nationalCode'])
                                forceUpdate({})
                                if (mobile.length === 11) {
                                    setLoadingUser(true)
                                    await getUser(mobile.substring(1))
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.firstName}
                        name="firstName"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.firstName} ${Validation.required}` },
                            { min: 3, message: `${TicketLanguage.firstName} ${Validation.min} 3 ${Validation.length}` },
                            { max: 30, message: `${TicketLanguage.firstName} ${Validation.max} 30 ${Validation.length}` },
                            { pattern: notEnglish, message: Validation.persian },
                        ]}
                    >
                        <Input maxLength={30} placeholder={TicketLanguage.firstName} />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.lastName}
                        name="lastName"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.lastName} ${Validation.required}` },
                            { min: 3, message: `${TicketLanguage.lastName} ${Validation.min} 3 ${Validation.length}` },
                            { max: 30, message: `${TicketLanguage.lastName} ${Validation.max} 30 ${Validation.length}` },
                            { pattern: notEnglish, message: Validation.persian },
                        ]}
                    >
                        <Input maxLength={30} placeholder={TicketLanguage.lastName} />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.nationalCode}
                        name="nationalCode"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.nationalCode} ${Validation.required}` },
                            { pattern: isNumber, message: `${TicketLanguage.nationalCode} ${Validation.pattern}` },
                            { len: 10, message: `${TicketLanguage.nationalCode} 10 ${Validation.length}` },
                        ]}
                    >
                        <Input type="tel" maxLength={10} placeholder={TicketLanguage.nationalCode} />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.destination}
                        name="destination"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.destination} ${Validation.required}` },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder={Share.pleaseSelectedRecords}
                            optionFilterProp="name"
                            onChange={async (destination) => {
                                form.resetFields(['company', 'amount']);
                                setCompanies([]);
                                setAmount(0);
                                destination && await getCompanies(destination);
                                destination && form.getFieldValue('seat') && await getDestination(destination, form.getFieldValue('seat'));
                            }}
                            options={provinces.map(({ name, cities }) => ({ label: name, options: cities.map(({ id, name }) => ({ label: name, value: id, name }))}))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.company}
                        name="company"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.company} ${Validation.required}` },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder={Share.pleaseSelectedRecord}
                            options={companies.map(({ id, name, position, priority, seatOne = 0, seatTwo = 0, seatThree = 0, seatLeft = 0, carLeft = 0, countService = 0}) => ({label: name, value: id, position, priority, seatOne, seatTwo, seatThree, seatLeft, carLeft, countService}))}
                            optionRender={(option) => {
                                const { data: { position, label, priority, seatOne, seatTwo, seatThree, seatLeft, carLeft, countService }} = option;
                                return (
                                    <Space direction="vertical" size={0}>
                                        <span style={{color: priority ? 'red' : 'unset'}}>{position} - {label} {priority ? <span style={{fontSize: 11}}>({TicketLanguage.priority(priority)})</span> : ''}</span>
                                        {seatLeft > 0 ? <span style={{fontSize: 11, color:'#9f8202'}}>{TicketLanguage.capacityTitle} {TicketLanguage.capacity(seatLeft, carLeft)}</span> : null}
                                        {countService > 0 ? <span style={{fontSize: 11, color:'#056505'}}>{TicketLanguage.countTitle} {TicketLanguage.count(countService)}</span> : null}
                                        {seatOne > 0 ? <span style={{fontSize: 11}}>{TicketLanguage.ticketTitle} {TicketLanguage.ticket(seatOne, 1)}</span> : null}
                                        {seatTwo > 0 ? <span style={{fontSize: 11}}>{TicketLanguage.ticketTitle} {TicketLanguage.ticket(seatTwo, 2)}</span> : null}
                                        {seatThree > 0 ? <span style={{fontSize: 11}}>{TicketLanguage.ticketTitle} {TicketLanguage.ticket(seatThree, 3)}</span> : null}
                                    </Space>
                                )
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.seat}
                        name="seat"
                        hasFeedback
                        rules={[
                            { required: true, message: `${TicketLanguage.seat} ${Validation.required}` },
                        ]}
                    >
                        <InputNumber
                            style={{width: '100%'}}
                            min={1}
                            max={3}
                            onChange={async (seat) => {
                                form.resetFields(['amount']);
                                setAmount(0);
                                seat && form.getFieldValue('destination') && await getDestination(form.getFieldValue('destination'), seat);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.outCycle}
                        name="outCycle"
                        valuePropName="checked"
                    >
                        <Switch

                            checkedChildren={Share.yes}
                            unCheckedChildren={Share.no}
                        />
                    </Form.Item>
                    <Form.Item
                        label={TicketLanguage.amount}
                        name="amount"
                        initialValue={amount}
                    >
                        <InputNumber
                            style={{width: '100%'}}
                            addonAfter={Share.rial}
                            disabled={true}
                            formatter={(value: any) => Math.trunc(Math.abs(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                    </Form.Item>

                    <Form.Item shouldUpdate>
                        {() => (
                            <Button
                                icon={<FaRegSave />}
                                type="primary"
                                htmlType="submit"
                                block
                                disabled={
                                    !!ticketId ||
                                    !form.getFieldValue('mobile') ||
                                    !form.getFieldValue('firstName') ||
                                    !form.getFieldValue('lastName') ||
                                    !form.getFieldValue('nationalCode') ||
                                    !form.getFieldValue('destination') ||
                                    !form.getFieldValue('company') ||
                                    !form.getFieldValue('seat') ||
                                    !!form.getFieldsError().filter(({ errors }) => errors.length).length
                                }
                            >
                                { Share.save }
                            </Button>
                        )}
                    </Form.Item>
                    {
                        ticketId && (
                            <Button
                                icon={<FaPrint />}
                                disabled={false}
                                block
                                type="primary"
                                danger
                                onClick={() => {
                                    setLoadingRecord(true)
                                    return getRecord(ticketId).then((ticket: TicketType) => {

                                        const width = 80;
                                        const height = 900;
                                        const doc = new jsPDF("p", "mm", [width, height]);
                                        doc.addFileToVFS("IRANSans.ttf", IRANSans);
                                        doc.addFont("IRANSans.ttf", "IRANSans", "normal");
                                        doc.addFileToVFS("IRANSansFaNum.ttf", IRANSansFaNum);
                                        doc.addFont("IRANSansFaNum.ttf", "IRANSansFaNum", "normal");
                                        doc.setLanguage("fa-IR");
                                        const x = 40;
                                        const x2 = 75;
                                        const x3 = 5;
                                        const x4 = 25;
                                        const w = 70;
                                        const w2 = 30;
                                        const h2 = 33;
                                        let y = 10;

                                        // doc.addImage(require('../../../resources/media/logo-resize/shahrdari.png'), x4, y, w2, h2);
                                        // y = y + 35;

                                        doc.setFont("IRANSansFaNum");
                                        doc.setFontSize(11);

                                        if (meta?.title) {
                                            doc.setTextColor("black");
                                            // @ts-ignore
                                            doc.text(meta?.title, x, y - 0.5, null, null, 'center');
                                            y = y + 3;
                                        }

                                        const body = (title: string, value: string, show: boolean) => {
                                            if (show) {
                                                doc.setTextColor("black");
                                                y = y + 5;
                                                // @ts-ignore
                                                doc.text(':' + title, x, y - 0.5, null, null, 'center');
                                                y = y + 7;
                                                // @ts-ignore
                                                doc.text(value, x, y, null, null, 'center');
                                                doc.rect(x3, y - 4, w, 6, "S");
                                                y = y + 3;
                                                doc.line(5, y, x2, y);
                                            }
                                        }

                                        for (const { title, value, show} of [
                                            {
                                                title: TicketLanguage.number,
                                                value: ticket.number.toString(),
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.date,
                                                value: dayjs(ticket.updatedAt).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD HH:mm'),
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.name,
                                                value: `${ticket.user.firstName} ${ticket.user.lastName}`,
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.nationalCode,
                                                value: ticket.user.nationalCode.toString(),
                                                show: true,
                                            },
                                            // {
                                            //     title: TicketLanguage.mobile,
                                            //     value: `0${ticket.user.mobile}`,
                                            //     show: true,
                                            // },
                                            {
                                                title: TicketLanguage.destination,
                                                value: `${ticket.destination.name} - ${ticket.destination.province.name}`,
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.seat,
                                                value: ticket.seat.toString(),
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.company,
                                                value: ticket.company.name,
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.tel,
                                                value: ticket.company.tel.toString(),
                                                show: true,
                                            },
                                            {
                                                title: TicketLanguage.amount,
                                                value: `${currency.format(amount/10)} ${TicketLanguage.toman}`,
                                                show: Boolean(amount),
                                            },
                                        ])
                                            body(title, value, show);

                                        y = y + 60;

                                        doc.line(2, y, 1, y);

                                        doc.autoPrint();
                                        doc.output("datauristring");
                                        doc.output("dataurlnewwindow");
                                        // doc.save(`ticket-${ticket.number}.pdf`);
                                    })
                                }}>{Share.print}</Button>
                        )
                    }
                </Form>
            </Spin>
        </div>
    )
}
